import { useCallback, useState } from 'react'
import { TextField, Typography, Autocomplete, Button, CircularProgress } from '@mui/material';
import { sxAutocomplete, sxButtonText, sxButtonVoltar, sxSubTitle, sxText, sxTextField } from './sxProps';
import './styles.css'
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { useAccessibility } from '../../../../Store/contexts/AccessibilityContext';
import { Procedimento } from '../../../../interfaces/entities/Procedimento';
import { ProcedimentoData } from '../../../../interfaces/entities/ProcedimentoData';
import { debounce } from '../../../../utils/Debounce';
import { getListaProced, getListaProcedDetalhe } from '../../../../services/Financeiro/ReembolsoService';
import ProcedimentoDetalhes from './ProcedimentoDetalhes';
import { Exec07Data } from '../../../../interfaces/entities/Exec07Data';
import { ReembolsoAtencaoDetalhesData } from '../../../../interfaces/entities/ReembolsoAtencaoDetalhesData';
import Exec07Detalhes from './Exec07Detalhes';
import AtencaoDetalhes from './AtencaoDetalhes';
import InfoComplementar from './InfoComplementar';
import ModalFeedBack from '../../../../components/ModalFeedBack';
import { onlyAlphanumeric } from '../../../../utils/OnlyAlphanumeric';
import AdviceContainer from '../../../../components/AdviceContainer';
import { TextTypeEnum } from '../../../../interfaces/enum/TextTypeEnum';


function ValorReembolso() {

  const navigate = useNavigate();
  const accessibility = useAccessibility();

  const [options, setOptions] = useState<Procedimento[]>([]);
  const [loadingOptions, setLoadingOptions] = useState<boolean>(false);
  const [loadingDetalhes, setLoadingDetalhes] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [selectedOption, setSelectedOption] = useState<Procedimento | null>(null);
  const [detalhes, setDetalhes] = useState<ProcedimentoData | null>();
  const [detalhesExec07, setDetalhesExec07] = useState<Exec07Data | null>(null);
  const [atencaoData, setAtencaoData] = useState<ReembolsoAtencaoDetalhesData | null>(null);
  const [infoComplementar, setInfoComplementar] = useState<string | null>(null);
  const [autocompleteNoOptionsText, setAutocompleteNoOptionsText] = useState<string>('Digite para pesquisar.')
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);

  const getAutocompleteOptions = useCallback(
    debounce(
      (search: string) => {
        if (search) {
          setLoadingOptions(true);
          getListaProced(search)
            .then(response => {
              setSelectedOption(null);
              setOptions(response);
              setAutocompleteNoOptionsText('Sem resultados para a busca.');
            })
            .catch(error => {
              console.log(error);
              setShowErrorModal(true);
            })
            .finally(() => {
              setLoadingOptions(false);
            })
        }
      }, 300), 
    [])

  const handleInputChange = (newInputValue: string) => {
    const cleanedInputValue = onlyAlphanumeric(newInputValue);
    setInputValue(cleanedInputValue);
    if (cleanedInputValue.length < 3) {
      setAutocompleteNoOptionsText('Digite para pesquisar.')
    }
    if (cleanedInputValue.length >= 3) {
      getAutocompleteOptions(cleanedInputValue);
    } else {
      setOptions([]);
    }
  }

  const handleChange = (option: Procedimento | null, reason: string) => {
    if (reason === 'selectOption') {
      handleOptionClick(option);
    } else if (reason === 'clear') {
      setAutocompleteNoOptionsText('Digite para pesquisar.')
      setInputValue('');
      setOptions([]);
    }
  }

  const handleOptionClick = (option: Procedimento | null) => {
    setSelectedOption(option);
    setInputValue(option?.descricao || '');
    if (option) {
      setLoadingDetalhes(true);
      getListaProcedDetalhe(option.id)
        .then(response => {
          if (response && response[0]) {
            const result = response[0];
            setDetalhes({
              codigo: result?.detalhesProced?.codTuss || '',
              procedimento: result?.detalhesProced?.procedimento || '',
              valor: result?.detalhesProced?.isExibeValorProced ? result?.detalhesProced?.valorProced : '',
              tooltipText: result?.fatorModerador?.isExibeFatorModerador ? result.fatorModerador.infoFatorModerador : ''
            })

            if (result?.detalhesExec07?.isExibeDetailsExec07) {
              const detalhesExec07 = result?.detalhesExec07;
              const valoresAuxiliares = detalhesExec07?.lstvlrAuxiliar
                ? detalhesExec07.lstvlrAuxiliar
                    .map((item: { vlrAuxiliar: any; }) => ({ valorAuxiliar: item.vlrAuxiliar }))
                : []
              setDetalhesExec07({
                valorCirurgiao: detalhesExec07?.vlrCirurgiao,
                valorAnestesista: detalhesExec07?.vlwAnestesista,
                valoresAuxiliares,
                valorTotal: detalhesExec07?.vlrTotal
              })
              setAtencaoData({
                title: detalhesExec07?.atencaoDetails?.title || '',
                text: detalhesExec07?.atencaoDetails?.descricao || ''
              })
            } else {
              setDetalhesExec07(null);
              setAtencaoData(null);
            }
            
            setInfoComplementar(result?.fatorModerador?.infoComplementar || null);
          }
        })
        .catch(error => {
          console.log(error);
          setShowErrorModal(true);
        })
        .finally(() => {
          setLoadingDetalhes(false);
        })
    }
  }  
  
  return (
    <div className="reembolso-valor-container">
      <h1 className='reembolso-valor-title' style={accessibility.textTitleStyleAccessibility}>Valor de reembolso</h1>
      <Typography sx={sxText} style={accessibility.textStyleAccessibility}>Para saber o valor máximo de reembolso, pesquise pelo nome do exame, procedimento ou preencha o código TUSS (Terminologia Unificada da Saúde Suplementar) que consta no pedido médico.</Typography>
      <AdviceContainer content={[{ text: 'Esta consulta não indica autorização para pagamento do reembolso.', type: TextTypeEnum.Regular}]} />
      <Autocomplete
        sx={sxAutocomplete}
        style={accessibility.textStyleAccessibility}
        options={options}
        getOptionLabel={option => option.descricao}
        value={selectedOption}
        onChange={(_, newValue, reason) => { handleChange(newValue, reason); }}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => { if (event && event.type === 'change') handleInputChange(newInputValue); }}
        loading={loadingOptions}
        noOptionsText={autocompleteNoOptionsText}
        renderInput={(params) => (
          <TextField
            { ...params }
            id="outlined-basic" 
            variant="outlined"
            sx={sxTextField}
            fullWidth />
          )}
      />
    { !loadingDetalhes && detalhes && <ProcedimentoDetalhes data={detalhes} /> }
    { !loadingDetalhes && infoComplementar && <InfoComplementar data={infoComplementar} />}
    { !loadingDetalhes && detalhesExec07 && <Exec07Detalhes data={detalhesExec07} /> }
    { !loadingDetalhes && atencaoData && <AtencaoDetalhes data={atencaoData} /> }
    {  loadingDetalhes && <div className="centered-div"><CircularProgress /></div> }
    <Button
        sx={sxButtonVoltar}
        onClick={() => navigate('/financeiro/reembolso')}
        startIcon={<FontAwesomeIcon style={accessibility.textStyleAccessibility} icon={faChevronLeft} color="#383838"/>}
        aria-label="Voltar para menu reembolso"
      >
        <Typography style={accessibility.textStyleAccessibility} sx={sxButtonText}>Voltar</Typography>
      </Button>
      <ModalFeedBack
        showModal={showErrorModal}
        handleShowModal={() => { setShowErrorModal(false); }}
        icon={'error'}
        textButton={''}
        title={'Atenção'}
        subTitle={'Erro ao buscar dados. Tente novamente mais tarde.'}
      />
    </div>
  )
}

export default ValorReembolso